import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import constants from "../../common/constants";
import { setLoginDetails } from "../../utility/SessionManager";
import { postData } from "../../services";

// Helper function for API calls
const apiCall = async (url, data, rejectWithValue) => {
  try {
    const response = await axios.post(url, data);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response.data);
  }
};

// Async Thunks for API Calls
export const sendOtp = createAsyncThunk(
  "auth/sendOtp",
  async (emailId, { rejectWithValue }) => {
    return apiCall(
      process.env.REACT_APP_TMD_BACKEND_URL + constants.API_SEND_OTP,
      { emailId },
      rejectWithValue
    );
  }
);

export const resendOtp = createAsyncThunk(
  "auth/resendOtp",
  async (emailId, { rejectWithValue }) => {
    return apiCall(
      process.env.REACT_APP_TMD_BACKEND_URL + constants.API_RE_SEND_OTP,
      { emailId },
      rejectWithValue
    );
  }
);

export const authenticateOtp = createAsyncThunk(
  "auth/authenticateOtp",
  async ({ emailId, otp }, { rejectWithValue }) => {
    return apiCall(
      process.env.REACT_APP_TMD_BACKEND_URL + constants.API_AUTHENTICATE_OTP,
      { grantType: "otp", emailId, otp },
      rejectWithValue
    );
  }
);

export const renewToken = createAsyncThunk(
  "auth/renewToken",
  async (refreshToken, { rejectWithValue }) => {
    return apiCall(
      process.env.REACT_APP_TMD_BACKEND_URL + constants.API_RENEW_TOKEN,
      { renewToken: refreshToken },
      rejectWithValue
    );
  }
);

export const logout = createAsyncThunk(
  "auth/logout",
  async (refreshToken, { rejectWithValue }) => {
    return await postData(
      constants.API_LOGOUT,
      { renewToken: refreshToken },
      rejectWithValue
    );
  }
);

// Slice
const authSlice = createSlice({
  name: "auth",
  initialState: {
    loading: false,
    error: null,
    success: null,
    otpSent: false,
    authToken: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
    setError: (state, action) => {
      state.error = action.payload.error.errorMessage;
    },
    setSuccess: (state, action) => {
      state.success = action.payload.response.message;
    },
  },
  extraReducers: (builder) => {
    const handlePending = (state) => {
      state.loading = true;
      state.error = null;
      state.success = null;
    };

    const handleRejected = (state, action, defaultMessage) => {
      state.loading = false;
      state.error = action.payload?.error?.errorMessage || defaultMessage;
      state.success = null;
    };

    const handleSuccess = (state, action, defaultMessage) => {
      state.loading = false;
      state.error = null;
      state.success = action.payload?.response?.message || defaultMessage;
    };

    builder
      // Send OTP
      .addCase(sendOtp.pending, handlePending)
      .addCase(sendOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.otpSent = true;
        handleSuccess(state, action, "Otp Sent Successfully");
      })
      .addCase(sendOtp.rejected, (state, action) => {
        handleRejected(state, action, "Failed to send OTP");
      })
      // Resend OTP
      .addCase(resendOtp.pending, handlePending)
      .addCase(resendOtp.fulfilled, (state, action) => {
        state.loading = false;
        handleSuccess(state, action, "Otp resent Successfully");
      })
      .addCase(resendOtp.rejected, (state, action) => {
        handleRejected(state, action, "Failed to resend OTP");
      })
      // Authenticate OTP
      .addCase(authenticateOtp.pending, handlePending)
      .addCase(authenticateOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.authToken = action.payload.token; // Assuming the API returns a token
        handleSuccess(state, action, "Authenticated Successfully");
      })
      .addCase(authenticateOtp.rejected, (state, action) => {
        handleRejected(state, action, "Failed to authenticate OTP");
      })
      // Renew Token
      .addCase(renewToken.pending, handlePending)
      .addCase(renewToken.fulfilled, (state, action) => {
        state.loading = false;
        state.authToken = action.payload.token; // Assuming the API returns a new token
        setLoginDetails(action.payload.response);
        handleSuccess(state, action, "Token Renewed Successfully");
      })
      .addCase(renewToken.rejected, (state, action) => {
        handleRejected(state, action, "Failed to renew token");
      })
      // Logout
      .addCase(logout.pending, handlePending)
      .addCase(logout.fulfilled, (state) => {
        state.loading = false;
        state.authToken = null;
      })
      .addCase(logout.rejected, (state, action) => {
        handleRejected(state, action, "Failed to logout");
      });
  },
});

export const { clearError, setError, clearSuccess, setSuccess } =
  authSlice.actions;
export default authSlice.reducer;
